/**
 * Property color: the universal visual cue for tenant identity across the
 * app (chat row left-edge accents, calendar bar chips, filter dots, etc.).
 *
 * Two-tier resolution: a stored `color` on the property if the admin has
 * picked one, otherwise a deterministic hash-based pick from the palette.
 * The hash fallback means rows synced before the admin assigns a color
 * still render with stable, distinguishable hues — never a generic gray.
 *
 * The palette uses Tailwind "-700" shades, deliberately darker than the
 * "-500" reservation status colors (see `lib/utils.ts:reservationStatusColors`)
 * so the property accent bar on a row doesn't read as a status signal. Each
 * color also passes WCAG-AA contrast with white foreground text for use as
 * a chip / badge background in the calendar.
 */

import { isLightColor, normalizeHexColor } from "@vidimo/shared";

export const PROPERTY_COLOR_PALETTE = [
  "#B91C1C", // red-700
  "#C2410C", // orange-700
  "#B45309", // amber-700
  "#4D7C0F", // lime-700
  "#15803D", // green-700
  "#0F766E", // teal-700
  "#0E7490", // cyan-700
  "#1D4ED8", // blue-700
  "#4338CA", // indigo-700
  "#7E22CE", // purple-700
  "#BE185D", // pink-700
  "#475569", // slate-600 (neutral anchor)
] as const;

/**
 * Deterministic fallback for properties without a stored color. Stable
 * across reloads (and across devices) because it only depends on the
 * property id. Same id → same color, always.
 */
export function hashToPaletteIndex(id: string): number {
  // FNV-1a 32-bit. Fast, no dependencies, good distribution for short ids.
  let hash = 0x81_1c_9d_c5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    hash = Math.imul(hash, 0x01_00_01_93) >>> 0;
  }
  return hash % PROPERTY_COLOR_PALETTE.length;
}

/**
 * Resolve a property's color, falling back to the deterministic palette
 * pick when no color has been stored yet *or* when the stored value is
 * malformed. The return is always a normalized `#rrggbb` literal, so
 * consumers can interpolate it directly into HTML / inline styles
 * without a separate sanitiser step. Defense-in-depth against admin-
 * controlled input that ends up in an `innerHTML` template.
 */
export function colorForProperty(property: {
  id: string;
  color?: string | null;
}): string {
  return (
    normalizeHexColor(property.color) ??
    PROPERTY_COLOR_PALETTE[hashToPaletteIndex(property.id)]
  );
}

/**
 * Pick a readable text color for any background. Uses the WCAG-friendly
 * relative-luminance heuristic — bright backgrounds get black text, dark
 * ones get white. Accepts shorthand (`#abc`) and full (`#aabbcc`) hex.
 *
 * Picking per background is what keeps a solid chip readable: the status
 * hexes are mid-tone brand colours and hardcoded white text fails AA on
 * most of them (amber #f59e0b reads 2.15:1 against white, well under the
 * 4.5:1 floor).
 *
 * Defaults to white on parse failure so an off-palette color from a
 * power user (custom hex) still renders something legible.
 */
export function getTextColorForBg(hex: string): "#000000" | "#FFFFFF" {
  return isLightColor(hex) ? "#000000" : "#FFFFFF";
}
